import axios from 'axios';
import cookieService from './cookie';
import router from '@/router';
import { useAuthStore } from '@/store/auth';
import Swal from 'sweetalert2';
import { captureError } from './sentry';

// Variáveis para controlar o estado da renovação de token
let isRefreshing = false;
let failedQueue = [];

// Função para processar fila de requisições que falharam
const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

// Configuração base da API
const apiClient = axios.create({
  baseURL:
    process.env.VUE_APP_API_BASE_URL ||
    'https://api.fornecedoresconfeccao.com.br/api/v1',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000, // 30 segundos
  withCredentials: true,
});

// Interceptor para adicionar token de autenticação
apiClient.interceptors.request.use(
  (config) => {
    const token = cookieService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratamento de erros e refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Tratar especificamente erros de rate limiting (429)
    if (error.response?.status === 429) {
      // Extrair informações do header de rate limit, se disponíveis
      const retryAfter = error.response.headers['retry-after'];
      const rateLimitReset = error.response.headers['x-ratelimit-reset'];

      // Calcular tempo para tentar novamente
      let retryTime = 60; // Default: 60 segundos
      if (retryAfter) {
        retryTime = parseInt(retryAfter, 10);
      } else if (rateLimitReset) {
        retryTime = Math.max(
          1,
          Math.ceil((parseInt(rateLimitReset, 10) * 1000 - Date.now()) / 1000)
        );
      }

      console.warn(
        `[API] Rate limit atingido para ${originalRequest.url}. Tente novamente em ${retryTime} segundos.`
      );

      // Mostrar alerta ao usuário
      Swal.fire({
        title: 'Muitas solicitações',
        text: `Por favor, aguarde ${retryTime} segundos antes de tentar novamente.`,
        icon: 'warning',
        confirmButtonText: 'Entendi',
        timer: retryTime * 1000,
        timerProgressBar: true,
      });

      // Enriquecer o objeto de erro com informações de retry
      error.retryAfter = retryTime;
      return Promise.reject(error);
    }

    // Erros de credenciais devem chegar ao formulário sem renovar a sessão.
    if (originalRequest.url.includes('/auth/login') && error.response) {
      return Promise.reject(error);
    }

    // Verificar se é um erro de autenticação e tentar renovar o token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Se já estamos renovando o token, adicione esta requisição à fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Marcar que estamos renovando o token e que esta requisição já tentou refresh
      originalRequest._retry = true;
      isRefreshing = true;

      // Usar o store de autenticação para renovar o token
      const authStore = useAuthStore();

      try {
        // Tentar renovar o token
        const result = await authStore.refreshToken();

        if (result && result.success) {
          // Renovação bem-sucedida
          const newToken = localStorage.getItem('accessToken');

          // Atualizar o token na requisição original
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          // Processar requisições em fila com o novo token
          processQueue(null, newToken);

          // Reiniciar estado de renovação
          isRefreshing = false;

          // Reenviar a requisição original com novo token
          return apiClient(originalRequest);
        } else {
          // Caso a renovação falhe, limpar sessão e redirecionar
          handleAuthFailure(
            'Sua sessão expirou. Por favor, faça login novamente.'
          );
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Erro na tentativa de refresh
        console.error('[API] Erro ao renovar token:', refreshError);

        // Registrar no Sentry
        captureError(refreshError, {
          context: 'token_refresh',
          originalRequest: `${originalRequest.method} ${originalRequest.url}`,
        });

        // Processar requisições em fila com erro
        processQueue(refreshError);
        isRefreshing = false;

        // Limpar sessão e mostrar mensagem
        handleAuthFailure(
          'Sua sessão expirou. Por favor, faça login novamente.'
        );
        return Promise.reject(refreshError);
      }
    }

    // Outros tratamentos de erro (erro 403, etc)
    if (error.response?.status === 403) {
      router.push({ name: 'access-denied' });
    }

    // Para erros 500 específicos de refresh token
    if (
      error.response &&
      error.response.status === 500 &&
      error.response.config.url.includes('/auth/refresh')
    ) {
      // Registrar no Sentry
      captureError(error, {
        context: 'refresh_endpoint_500',
        url: error.response.config.url,
        status: error.response.status,
      });

      // Tratar como erro de autenticação
      handleAuthFailure(
        'Não foi possível renovar sua sessão. Por favor, faça login novamente.'
      );
      return Promise.reject(error);
    }

    // Para outros erros de servidor, mostra mensagens genéricas mais amigáveis
    if (error.response && error.response.status >= 500) {
      console.error('[API] Erro de servidor:', error);

      // Registrar erros de servidor no Sentry
      captureError(error, {
        context: 'server_error',
        url: error.config.url,
        method: error.config.method,
        status: error.response.status,
      });
    }

    return Promise.reject(error);
  }
);

// Função para tratar falha de autenticação
const handleAuthFailure = () => {
  // Limpar tokens e dados de autenticação
  const authStore = useAuthStore();

  // Verificar se o método existe antes de chamar
  if (typeof authStore.clearAuthData === 'function') {
    authStore.clearAuthData();
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Limpar também os cookies
    document.cookie =
      'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    // Se houver uma função de invalidação de cache na store
    if (typeof authStore.invalidateCache === 'function') {
      authStore.invalidateCache();
    }
  }

  // Não tentar fazer logout no servidor, pois isso pode gerar um loop de requisições
  // Em vez disso, mostrar uma mensagem para o usuário

  // Verificar se estamos na página de login
  if (router.currentRoute.value.name === 'login') {
    return;
  }
};

// Definição dos endpoints da API
const api = {
  // Autenticação
  auth: {
    login: (data) => apiClient.post('/auth/login', data),
    register: (data) => apiClient.post('/auth/register', data),
    logout: () => apiClient.post('/auth/logout'),
    getUser: () => apiClient.get('/auth/user'),
    refreshToken: (token) => apiClient.post('/auth/refresh', { refreshToken: token }),
    forgotPassword: (email) =>
      apiClient.post('/auth/forgot-password', { email }),
    resetPassword: (data) => apiClient.post('/auth/reset-password', data),
    validateMagicLink: (tokenHash) =>
      apiClient.post(
        '/auth/magic-link/validate',
        {
          token_hash: tokenHash,
        },
        {
          // Adicionar log para diagnóstico
          transformResponse: [
            (data) => {
              let parsedData;
              try {
                parsedData = JSON.parse(data);
              } catch (e) {
                console.error('[API] Erro ao analisar resposta:', e);
                return data;
              }
              return parsedData;
            },
            ...axios.defaults.transformResponse,
          ],
        }
      ),
    resendMagicLink: (email) =>
      apiClient.post('/auth/resend-magic-link', { email }),
    updatePassword: (data) => apiClient.post('/auth/update-password', data),
    updatePasswordFirstLogin: (data) =>
      apiClient.post('/auth/update-password-first-login', data, {
        // Adicionar log para diagnóstico
        transformResponse: [
          (data) => {
            let parsedData;
            try {
              parsedData = JSON.parse(data);
            } catch (e) {
              console.error('[API] Erro ao analisar resposta:', e);
              return data;
            }
            return parsedData;
          },
          ...axios.defaults.transformResponse,
        ],
      }),
    requestMagicLink: (data) => apiClient.post('/auth/resend-magic-link', data),
    checkEmail: (email) => apiClient.post('/auth/check-email', { email }),
    validateResetToken: (token) =>
      apiClient.post('/auth/validate-reset-token', {
        token_hash: token,
      }),
  },

  // Contas de Usuários (Accounts)
  accounts: {
    getAll: (params = {}) => apiClient.get('/accounts', { params }),
    getRoles: () => apiClient.get('/accounts/roles'),
    getMe: () => apiClient.get('/accounts/me'),
    getById: (id) => apiClient.get(`/accounts/${id}`),
    update: (id, data) => apiClient.patch(`/accounts/${id}`, data),
    delete: (id) => apiClient.delete(`/accounts/${id}`),
    //deletePermanent: (id) => apiClient.delete(`/accounts/${id}/permanent`),
    changePassword: (data) => apiClient.post('/accounts/change-password', data),
  },

  // Fornecedores
  suppliers: {
    getAll: (params = {}) => apiClient.get('/suppliers', { params }),
    getById: (id) => apiClient.get(`/suppliers/${id}`),
    create: (data) => {
      // Verificar se estamos recebendo um FormData
      const isFormData = data instanceof FormData;

      return apiClient.post('/suppliers', data, {
        headers: isFormData
          ? {
              'Content-Type': 'multipart/form-data',
            }
          : {
              'Content-Type': 'application/json',
            },
      });
    },
    update: (id, data) => {
      // Verificar se estamos recebendo um FormData
      const isFormData = data instanceof FormData;

      return apiClient.patch(`/suppliers/${id}`, data, {
        headers: isFormData
          ? {
              'Content-Type': 'multipart/form-data',
            }
          : {
              'Content-Type': 'application/json',
            },
      });
    },
    delete: (id) => apiClient.delete(`/suppliers/${id}`),

    // Método para importação de fornecedores via CSV
    importCsv: (formData) =>
      apiClient.post('/admin/suppliers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),

    // Método para atualizar metadados de uma imagem específica (posição, descrição, capa)
    updateImageMetadata: (supplierId, imageId, metadata) =>
      apiClient.patch(`/suppliers/${supplierId}/images/${imageId}`, metadata),

    // Método para atualizar a ordem de múltiplas imagens de uma vez
    updateImagesOrder: (supplierId, imagesOrder) =>
      apiClient.patch(`/suppliers/${supplierId}/images-order`, { imagesOrder }),

    // Método para obter todas as imagens de um fornecedor
    getImages: (supplierId) => apiClient.get(`/suppliers/${supplierId}/images`),

    // Método para adicionar novas imagens a um fornecedor existente
    addImages: (supplierId, data) => {
      // Verificar se estamos recebendo um FormData
      const isFormData = data instanceof FormData;

      return apiClient.post(`/suppliers/${supplierId}/images`, data, {
        headers: isFormData
          ? {
              'Content-Type': 'multipart/form-data',
            }
          : {
              'Content-Type': 'application/json',
            },
      });
    },
  },

  // Categorias
  categories: {
    getAll: (params = {}) => apiClient.get('/categories', { params }),
    getById: (id, params = {}) =>
      apiClient.get(`/categories/${id}`, { params }),
    create: (data) => apiClient.post('/categories', data),
    update: (id, data) => apiClient.patch(`/categories/${id}`, data),
    delete: (id) => apiClient.delete(`/categories/${id}`),
    importCsv: (formData) =>
      apiClient.post('/admin/categories/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
  },

  // Denúncias (Reports)
  reports: {
    // Criar uma nova denúncia
    create: (data) => apiClient.post('/reports', data),

    // Obter todas as denúncias (somente admin)
    getAll: (params = {}) => apiClient.get('/reports', { params }),

    // Obter denúncias do usuário atual
    getUserReports: (params = {}) =>
      apiClient.get('/reports/my-reports', { params }),

    // Obter detalhes de uma denúncia específica
    getById: (id) => apiClient.get(`/reports/${id}`),

    // Atualizar o status de uma denúncia (somente admin)
    updateStatus: (id, data) => apiClient.patch(`/reports/${id}`, data),

    // Obter relatórios por tipo de entidade
    getByEntity: (entityType, entityId, params = {}) =>
      apiClient.get(`/reports/entity/${entityType}/${entityId}`, { params }),
  },

  // Upload de arquivos
  upload: {
    // Upload de avatar do usuário
    avatar: (file, onProgress) => {
      const formData = new FormData();
      formData.append('file', file);

      return apiClient.post('/uploads/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });
    },

    // Upload de imagens para fornecedores
    supplierImages: (files, supplierId, imageMetadata = [], onProgress) => {
      const formData = new FormData();

      // Adicionar arquivos
      files.forEach((file) => {
        formData.append('files', file);
      });

      // Adicionar ID do fornecedor
      formData.append('supplierId', supplierId);

      // Adicionar metadados das imagens (se houver)
      if (imageMetadata && imageMetadata.length > 0) {
        formData.append('imageMetadata', JSON.stringify(imageMetadata));
      }

      return apiClient.post('/uploads/suppliers', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });
    },

    // Excluir uma imagem de fornecedor
    deleteSupplierImage: (supplierId, imageId) =>
      apiClient.delete(`/suppliers/${supplierId}/images/${imageId}`),
  },

  // Dashboard
  dashboard: {
    getSummary: () => apiClient.get('/dashboard/summary'),
    getTopSuppliers: () => apiClient.get('/dashboard/top-suppliers'),
    getRecentActivity: () => apiClient.get('/dashboard/recent-activity'),
  },

  // Analytics Admin
  analytics: {
    // Visão geral com métricas
    getOverview: (period = 'month') => 
      apiClient.get(`/admin/analytics/overview`, { params: { period } }),

    // Visão geral para o dashboard do usuário
    getUserOverview: () => 
      apiClient.get(`/admin/analytics/user/overview`),
    
    // Top fornecedores mais vistos
    getTopViewedSuppliers: (period = 'month') => 
      apiClient.get(`/admin/analytics/suppliers/top-viewed`, { params: { period } }),
    
    // Localizações mais buscadas
    getTrendingLocations: (period = 'month') => 
      apiClient.get(`/admin/analytics/locations/trending`, { params: { period } }),
    
    // Categorias mais buscadas
    getTrendingCategories: (period = 'month') => 
      apiClient.get(`/admin/analytics/categories/trending`, { params: { period } }),
    
    // Timeline de novos fornecedores
    getSuppliersTimeline: () => 
      apiClient.get(`/admin/analytics/suppliers/timeline`),
  },

  // Plausible Site Analytics (Self-hosted)
  siteAnalytics: {
    // Verificar se o Plausible está configurado e listar sites disponíveis
    getStatus: () => apiClient.get('/analytics/plausible/status'),
    
    // Listar sites disponíveis
    getSites: () => apiClient.get('/analytics/plausible/sites'),
    
    // Visitantes em tempo real
    getRealtime: (siteId = null) => 
      apiClient.get('/analytics/plausible/realtime', { params: siteId ? { siteId } : {} }),
    
    // Estatísticas agregadas (visitors, pageviews, bounce_rate, visit_duration)
    getAggregate: (period = '30d', siteId = null) => 
      apiClient.get('/analytics/plausible/aggregate', { params: { period, ...(siteId ? { siteId } : {}) } }),
    
    // Dados de série temporal para gráficos
    getTimeseries: (period = '30d', siteId = null) => 
      apiClient.get('/analytics/plausible/timeseries', { params: { period, ...(siteId ? { siteId } : {}) } }),
    
    // Breakdown por dimensão (pages, sources, countries, devices, browsers)
    getBreakdown: (dimension, period = '30d', limit = 10, siteId = null) => 
      apiClient.get(`/analytics/plausible/breakdown/${dimension}`, { 
        params: { period, limit, ...(siteId ? { siteId } : {}) } 
      }),
  },

  // Fornecedores Pendentes
  pendingSuppliers: {
    getAll: (params = {}) =>
      apiClient.get('/admin/pending-suppliers', { params }),
    getById: (id) => apiClient.get(`/admin/pending-suppliers/${id}`),
    approve: (id, data) =>
      apiClient.post(`/admin/pending-suppliers/${id}/approve`, data),
    reject: (id, data) =>
      apiClient.post(`/admin/pending-suppliers/${id}/reject`, data),
  },

  // Ratings
  ratings: {
    // Criar uma nova avaliação
    create: (data) => apiClient.post('/ratings', data),

    // Obter todas as avaliações
    getAll: (params = {}) => apiClient.get('/ratings', { params }),

    // Obter avaliação específica
    getById: (id) => apiClient.get(`/ratings/${id}`),

    // Obter avaliações de um fornecedor específico
    getBySupplierId: (supplierId) => apiClient.get(`/ratings/supplier/${supplierId}`),

    // Atualizar avaliação
    update: (id, data) => apiClient.patch(`/ratings/${id}`, data),

    // Excluir avaliação
    delete: (id) => apiClient.delete(`/ratings/${id}`),
  },

  // Assinaturas (Subscriptions)
  subscriptions: {
    // Obter todas as assinaturas (admin)
    getAll: (params = {}) => apiClient.get('/payments/admin/subscriptions', { params }),

    // Obter assinatura por ID
    getById: (id) => apiClient.get(`/payments/subscriptions/${id}`),

    // Obter assinatura pelo ID do usuário (admin)
    getByUserId: (userId) => apiClient.get(`/payments/admin/subscriptions/user/${userId}`),

    // Obter assinatura pelo email (admin)
    getByEmail: (email) => apiClient.get(`/payments/admin/subscriptions/email/${encodeURIComponent(email)}`),

    // Reenviar magic link para usuário (admin)
    resendMagicLink: (email) => apiClient.post('/auth/resend-magic-link', { email }),
  },
};

export default api;
